/**
 * T4 统一通知中心 - 前端类型 + 文案模板。
 *
 * 与 server/internal/notification/types.go 镜像，但前端还要承担：
 *  - subject_type + type → 跳转路由（routeTargetForNotification）
 *  - type → 中文短标题 + 图标（subjectTitle / subjectIcon）
 *  - payload_json → 详情扩展（status / subject_type / reject_reason）
 *
 * payload_json 解析走 try/catch；解析失败时退化为 {}，调用方不抛错。
 */

import type {
  NotificationSubjectType,
  NotificationType,
  NotificationView,
} from '../../shared/api/types';

export type {
  NotificationSubjectType,
  NotificationType,
  NotificationView,
} from '../../shared/api/types';

export type NotificationCategory = 'system' | 'todo' | 'space';

export const NOTIFICATION_CATEGORY_LABEL: Record<NotificationCategory, string> = {
  system: '系统',
  todo: '待办',
  space: '空间',
};

export const NOTIFICATION_CATEGORY_DESCRIPTION: Record<NotificationCategory, string> = {
  system: '重要提醒，只读',
  todo: '需要你处理的事项',
  space: '本空间的重要动态',
};

/** 解析后的 payload；后端可能写任意键值，前端只取自己关心的字段。 */
export interface NotificationPayload {
  status?: 'approved' | 'rejected' | 'cancelled' | 'expired' | 'invitee_accepted' | 'owner_confirmed' | 'owner_abandoned';
  subject_type?: NotificationSubjectType;
  reject_reason?: string;
  invitation_id?: string;
  stage?: 'invitee' | 'owner';
  attempt?: number;
  available_actions?: string[];
  expires_at?: number;
}

export function parsePayload(raw: string | null): NotificationPayload {
  if (!raw) return {};
  try {
    const obj = JSON.parse(raw);
    if (obj && typeof obj === 'object') return obj as NotificationPayload;
    return {};
  } catch {
    return {};
  }
}

/** 将后端通知类型归入通知中心的三种用户语义。未知类型默认只读，避免误展示操作入口。 */
export function notificationCategory(n: NotificationView): NotificationCategory {
  switch (n.type) {
    case 'delete_request_filed':
    case 'space_invitation_received':
    case 'space_invitation_update':
      return 'todo';
    case 'content_published':
    case 'meet_story_updated':
      return 'space';
    case 'system_update':
    case 'delete_request_resolved':
    default:
      return 'system';
  }
}

export function isActionableNotification(n: NotificationView): boolean {
  return notificationCategory(n) !== 'system';
}

/** 跳转目标路由；按 (type, subject_type) 二元组映射。
 *  落在 /manage/approvals（审批）/ /letters/:id（信件）/ /photo-memories/:id（照片回忆）
 *  系统消息与无法识别的 subject 退回首页，让用户知道有通知但不阻塞。 */
export function routeTargetForNotification(n: NotificationView): string {
  if (n.type === 'system_update') return '/';
  if (n.type === 'content_published') {
    if (n.subject_type === 'letter') return `/letters/${encodeURIComponent(n.subject_id)}`;
    if (n.subject_type === 'photo_memory') return `/album#${encodeURIComponent(n.subject_id)}`;
    return '/';
  }
  if (n.type === 'meet_story_updated') return '/meet';
  // delete_request_* 都指向审批详情
  if (n.subject_type === 'approval_request') {
    // 审批中心由 owner 和 member 共同使用；旧的 /manage/inbox/:id
    // 没有注册路由，点击通知会落到全局首页，导致内容所有者无法处理请求。
    return '/manage/approvals';
  }
  return '/';
}

const TYPE_LABEL: Record<NotificationType, string> = {
  system_update: '系统公告',
  delete_request_filed: '新的审批申请',
  delete_request_resolved: '审批结果',
  content_published: '新内容',
  space_invitation_received: '空间邀请',
  space_invitation_update: '空间邀请进展',
  meet_story_updated: '相识相遇更新',
};

/** 简短标题（用于列表行第一行；summary 作为副标题）。
 *  例如 "审批结果" / "新内容"。 */
export function subjectTitle(n: NotificationView): string {
  return TYPE_LABEL[n.type] ?? '通知';
}

const SUBJECT_ICON: Record<NotificationType, 'bell' | 'letter' | 'camera'> = {
  system_update: 'bell',
  delete_request_filed: 'letter',
  delete_request_resolved: 'bell',
  content_published: 'camera',
  space_invitation_received: 'bell',
  space_invitation_update: 'bell',
  meet_story_updated: 'bell',
};

export function subjectIcon(n: NotificationView): 'bell' | 'letter' | 'camera' {
  return SUBJECT_ICON[n.type] ?? 'bell';
}

/** UI 列表分页大小；与 server list 默认 limit 保持一致。 */
export const NOTIFICATION_PAGE_SIZE = 20;

/** 时间相对展示（例如 "刚刚" / "3 分钟前" / "昨天"）。 */
export function relativeTime(ts: number, now: number = Date.now()): string {
  const diff = Math.max(0, now - ts);
  const sec = Math.floor(diff / 1000);
  if (sec < 30) return '刚刚';
  if (sec < 60) return `${sec} 秒前`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 2) return '昨天';
  if (day < 30) return `${day} 天前`;
  const date = new Date(ts);
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}
